import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import InkStroke from '../../components/base/InkStroke';

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

/* ─── Hero ─── */
function HeroSection() {
  const { ref, visible } = useReveal();
  return (
    <section className="relative h-[calc(100vh-72px)] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="/assets/hero.png"
          alt="Natto bowl with tea and soy sauce on a dark kitchen counter — wide cinematic dawn scene"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
      </div>

      <div
        ref={ref}
        className={`relative z-10 text-center px-6 md:px-10 max-w-3xl mx-auto transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <p className="font-body text-xs uppercase tracking-wide-custom text-parchment/70 mb-4">
          Discover
        </p>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-parchment leading-tight tracking-tight-custom text-balance">
          The food behind our name
        </h1>
        <InkStroke className="mt-8 mb-8" />
        <p className="font-body text-sm md:text-base text-parchment/70 max-w-xl mx-auto leading-relaxed">
          Why a Japanese fermented soybean dish sits at the heart of a consultancy brand — and what it says about the way we work.
        </p>
      </div>
    </section>
  );
}

/* ─── What is Natto ─── */
function WhatIsSection() {
  const { ref, visible } = useReveal();
  return (
    <section className="py-20 md:py-28 px-6 md:px-10 lg:px-16">
      <div className="max-w-5xl mx-auto">
        <div
          ref={ref}
          className={`grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="order-2 md:order-1">
            <p className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-3">
              What is Natto?
            </p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-medium text-deep-ink tracking-tight-custom mb-6">
              Fermented soybeans with a quiet strength
            </h2>
            <InkStroke align="left" className="mb-6" />
            <p className="font-body text-sm text-muted-umber leading-relaxed mb-4">
              Natto is a traditional Japanese food made from fermented soybeans. At first glance, it looks simple — even unremarkable. But it carries a complex depth of flavour and remarkable nutritional value.
            </p>
            <p className="font-body text-sm text-muted-umber leading-relaxed mb-4">
              It is known for its slightly pungent aroma and sticky texture, which comes from the delicate strings that form when it is stirred. Those same strings are rich in protein, vitamins, and beneficial bacteria that support long-term health.
            </p>
            <p className="font-body text-sm text-muted-umber leading-relaxed">
              In many Japanese households, natto is a small, everyday presence: a bowl on the breakfast table, mixed with rice, mustard, or soy sauce. It is modest in appearance, but it quietly supports the body over time.
            </p>
          </div>

          <div className="order-1 md:order-2">
            <div className="rounded-lg overflow-hidden border border-amber/10">
              <img
                src="/assets/whatis.png"
                alt="Natto: traditional Japanese breakfast scene with rice, natto, and soy sauce on a wooden tray"
                className="w-full h-auto object-cover object-top"
              />
            </div>
            <p className="font-body text-xs text-muted-umber/60 mt-3 text-center">
              A typical Japanese household breakfast: natto and rice
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Hidden in Plain Sight ─── */
function HiddenSection() {
  const { ref, visible } = useReveal();
  const imgRef = useRef<HTMLDivElement>(null);
  const [imgVisible, setImgVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setImgVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, []);

  const nutrients = [
    { label: 'Protein', value: '17g per 100g', desc: 'Complete plant protein with all essential amino acids' },
    { label: 'Vitamin K2', value: 'Exceptional', desc: 'Critical for bone health and cardiovascular function' },
    { label: 'Nattokinase', value: 'Unique enzyme', desc: 'Supports healthy blood circulation' },
    { label: 'Probiotics', value: 'Live cultures', desc: 'Promotes gut health and digestion' },
  ];

  return (
    <section className="py-20 md:py-28 px-6 md:px-10 lg:px-16 bg-white/40">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div
            ref={imgRef}
            className={`transition-all duration-700 ${
              imgVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="rounded-lg overflow-hidden border border-amber/10">
              <img
                src="/assets/plainsight.png"
                alt="A close-up photograph taken from inside a well-stocked household refrigerator shows a middle shelf stacked with generic white plastic containers alongside various labeled Japanese food items."
                className="w-full h-auto object-cover object-top"
              />
            </div>
            <p className="font-body text-xs text-muted-umber/60 mt-3 text-center">
              Natto sits quietly alongside rice and miso soup in millions of Japanese homes
            </p>
          </div>

          <div
            ref={ref}
            className={`transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <p className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-3">
              Hidden in Plain Sight
            </p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-medium text-deep-ink tracking-tight-custom mb-6">
              Unremarkable on the shelf, transformative in effect
            </h2>
            <InkStroke align="left" className="mb-6" />
            <p className="font-body text-sm text-muted-umber leading-relaxed mb-4">
              Natto rarely calls attention to itself. It is not a showpiece dish, and you will not usually find it in glossy food photography. Yet people who grow up with it understand its value instinctively: it is something you rely on more than you talk about.
            </p>
            <p className="font-body text-sm text-muted-umber leading-relaxed mb-4">
              That is what we appreciate about natto, and why it felt right for our name. It represents the kind of work that matters most to us — the sort that may not look dramatic from the outside, but fundamentally strengthens what it touches.
            </p>
            <p className="font-body text-sm text-muted-umber leading-relaxed mb-8">
              Just as natto supports the body quietly from within, we aim to support organisations in a way that is steady, sustaining, and built for the long term.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {nutrients.map((n, i) => (
                <div
                  key={n.label}
                  className={`bg-parchment/60 rounded-lg p-4 border border-amber/10 transition-all duration-700 ${
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: `${400 + i * 100}ms` }}
                >
                  <p className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-1">
                    {n.label}
                  </p>
                  <p className="font-body text-sm font-semibold text-deep-ink mb-1">
                    {n.value}
                  </p>
                  <p className="font-body text-xs text-muted-umber leading-relaxed">
                    {n.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── The String That Connects ─── */
function StickySection() {
  const { ref, visible } = useReveal();
  return (
    <section className="py-20 md:py-28 px-6 md:px-10 lg:px-16">
      <div className="max-w-5xl mx-auto">
        <div
          ref={ref}
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-3">
            The Texture
          </p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-medium text-deep-ink tracking-tight-custom">
            The string that connects everything
          </h2>
          <InkStroke className="mt-6" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div
            className={`transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="rounded-lg overflow-hidden border border-amber/10">
              <img
                src="/assets/texture.png"
                alt="Natto beans lifted by chopsticks with gossamer strings — dark, cinematic editorial shot"
                className="w-full h-auto object-cover object-top"
              />
            </div>
            <p className="font-body text-xs text-muted-umber/60 mt-3 text-center">
              The iconic neba-neba texture — sticky, stringy, and unmistakable
            </p>
          </div>

          <div
            className={`transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '400ms' }}
          >
            <p className="font-body text-sm text-muted-umber leading-relaxed mb-4">
              The Japanese have a word for natto’s sticky strands : <em>neba-neba</em>. When you stir natto, these fine strings appear and stretch between each bean, connecting everything in the bowl.
            </p>
            <p className="font-body text-sm text-muted-umber leading-relaxed mb-4">
              For us, that image matters. It mirrors the invisible threads inside organisations: the relationships, habits, and informal ways of working that hold everything together. When those connections are healthy, the whole system becomes stronger.
            </p>
            <p className="font-body text-sm text-muted-umber leading-relaxed">
              Our work is often about those threads. We pay attention to how people collaborate, how information travels, and how decisions actually get made day to day. When we support clients, we are not just changing structures or processes; we are helping to strengthen the connections that make those changes last.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Around the World ─── */
function GlobalSection() {
  const { ref, visible } = useReveal();

  const dishes = [
    {
      title: 'Natto Pasta (Natto Spaghetti)',
      desc: 'Spaghetti tossed with natto, soy sauce, butter, and a raw egg yolk — the heat of the pasta gently warming the beans, the strings clinging to every strand. Born from Japanese home cooking\'s willingness to borrow freely from Italian tradition, this dish raises eyebrows and then raises expectations. It is unexpectedly harmonious.',
      image:
        '/assets/pasta.png',
      alt: 'Natto spaghetti with butter, soy sauce, and egg yolk — Japanese-Italian fusion pasta in a white ceramic bowl',
    },
    {
      title: 'Natto Avocado Toast',
      desc: 'Sourdough toast spread with mashed avocado, topped with a generous spoonful of stirred natto, a squeeze of lemon, shichimi pepper, and toasted nori. A dish that would have seemed bizarre fifteen years ago, now common in Tokyo cafés and plant-based kitchens worldwide. Natto adapts without losing itself.',
      image:
        '/assets/toastie.png',
      alt: 'Natto avocado toast — a Japanese-inspired fusion dish combining fermented soybeans and smashed avocado on sourdough',
    },
    {
      title: 'Natto Fritters',
      desc: 'Natto fritters are crispy, savory Japanese snacks or appetizers made by combining fermented soybeans with a batter or grated vegetables, then pan-frying or deep-frying until golden. The cooking process mellows natto\'s pungent aroma and creates a delightfully crispy texture.',
      image:
        '/assets/fritters.png',
      alt: 'Golden crispy natto fritters with spring onion and sesame, served with soy dipping sauce',
    },
    {
      title: 'Natto Omelette',
      desc: 'The rich, gooey texture of natto pairs beautifully with eggs, making it perfect for a European-style breakfast or light lunch.',
      image:
        '/assets/natto-omelette.png',
      alt: 'Natto omelette with avocado, garlic, tomato, and cilantro on a white ceramic plate',
    },
  ];

  return (
    <section className="py-20 md:py-28 px-6 md:px-10 lg:px-16 bg-white/40">
      <div className="max-w-5xl mx-auto">
        <div
          ref={ref}
          className={`text-center mb-14 md:mb-20 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-3">
            Natto travels well
          </p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-medium text-deep-ink tracking-tight-custom">
            Beyond the breakfast table
          </h2>
          <InkStroke className="mt-6" />
          <p className="font-body text-sm text-muted-umber max-w-lg mx-auto mt-6 leading-relaxed">
            What began as a traditional breakfast food now appears in all kinds of unexpected dishes: stirred into pasta sauces, spread on toast, folded into salads, or baked onto pizza. It adapts, combines, and finds new roles far beyond its original setting.
          </p>
          <p className="font-body text-sm text-muted-umber max-w-lg mx-auto mt-6 leading-relaxed">
            That flexibility is another reason we identify with natto. In the same way, our work is designed to travel well — fitting into different cultures, sectors, and team structures without losing its character. We mix with what is already there, bring our own strengths to the table, and create something new together.
          </p>
          <p className="font-body text-sm text-muted-umber max-w-lg mx-auto mt-6 leading-relaxed">
            Like natto paired with rice, pasta, or bread, the most interesting outcomes appear when different ingredients combine. That is where we believe real, long-term value is made.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
          {dishes.map((dish, i) => (
            <div
              key={dish.title}
              className={`transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${200 + i * 150}ms` }}
            >
              <div className="bg-parchment/60 rounded-lg overflow-hidden border border-amber/10 h-full flex flex-col">
                <div className="aspect-square overflow-hidden">
                  <img
                    src={dish.image}
                    alt={dish.alt}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5 md:p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-lg md:text-xl font-medium text-deep-ink mb-2">
                    {dish.title}
                  </h3>
                  <p className="font-body text-sm text-muted-umber leading-relaxed flex-1">
                    {dish.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Closing ─── */
function ClosingSection() {
  const { ref, visible } = useReveal();
  return (
    <section className="py-20 md:py-28 px-6 md:px-10 lg:px-16">
      <div className="max-w-3xl mx-auto text-center">
        <div
          ref={ref}
          className={`transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="bg-white/60 rounded-lg p-8 md:p-12 border border-amber/10">
            <blockquote>
              <p className="font-display text-lg md:text-xl font-medium italic text-saddlebrown leading-relaxedß">
                &ldquo;Natto does not try to be something it is not. It is unapologetically
                itself — pungent, sticky, nutritious, and deeply connected to the
                culture that created it. We think that is a fine thing to aspire to.&rdquo;
              </p>
            </blockquote>
          </div>

          <div className="mt-12">
            <a
              href="/"
              className="inline-flex items-center gap-2 font-body text-sm font-semibold text-deep-ink hover:text-amber transition-colors duration-200"
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-arrow-left-line text-lg" />
              </span>
              <span>back to nattō studio</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Page Shell ─── */
export default function NattoPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The food behind our name",
    "description": "Why a Japanese fermented soybean dish sits at the heart of a consultancy brand.",
    "author": { "@type": "Organization", "name": "nattō studio" },
    "publisher": { "@type": "Organization", "name": "nattō studio" }
  };

  return (
    <main className="bg-parchment">
      <Helmet>
        <title>The food behind our name — nattō studio</title>
        <meta name="description" content="Why a Japanese fermented soybean dish sits at the heart of a consultancy brand, and what it says about how we work." />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <HeroSection />
      <WhatIsSection />
      <HiddenSection />
      <StickySection />
      <GlobalSection />
      <ClosingSection />
    </main>
  );
}